import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, Button, Box, Typography,
  TextField, Alert, Slide, Stepper, Step, StepLabel, Fade,
  IconButton, InputAdornment, Zoom
} from '@mui/material';
import {
  ArrowBack, CheckCircle, Lock, Person, Badge, VpnKey,
  Visibility, VisibilityOff
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { usePasswordReset } from '../hooks/usePasswordReset';
import { PasswordResetStep, PasswordResetAPI } from '../types/passwordResetTypes';

const PASSWORD_RESET_STEPS: PasswordResetStep[] = [
  { step: 1, title: 'Введите логин', icon: <Person /> },
  { step: 2, title: 'Проверка данных', icon: <Badge /> },
  { step: 3, title: 'Новый пароль', icon: <VpnKey /> },
  { step: 4, title: 'Готово', icon: <CheckCircle /> }
];

interface PasswordResetDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (login: string) => void;
  api?: PasswordResetAPI;
}

const PasswordResetDialog: React.FC<PasswordResetDialogProps> = ({
  open,
  onClose,
  onComplete,
  api
}) => {
  const { state, actions, validation } = usePasswordReset(api);

  const currentStepData = PASSWORD_RESET_STEPS.find(step => step.step === state.currentStep) || PASSWORD_RESET_STEPS[0];

  const handleClose = () => {
    actions.reset();
    onClose();
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete(state.formData.login);
    }
    handleClose();
  };

  const canProceed = () => {
    switch (state.currentStep) {
      case 1: return validation.canProceedStep1;
      case 2: return validation.canProceedStep2;
      case 3: return validation.canProceedStep3;
      default: return true;
    }
  };

  const renderStepContent = () => {
    switch (state.currentStep) {
      case 1:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
              Введите ваш логин
            </Typography>
            <TextField
              label="Логин"
              variant="outlined"
              fullWidth
              value={state.formData.login}
              onChange={(e) => actions.setLogin(e.target.value)}
              disabled={state.loading}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))',
                  borderRadius: '12px',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 10px rgba(99, 102, 241, 0.15)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.9))',
                  },
                  '&.Mui-focused': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.95))',
                    '& fieldset': {
                      border: '2px solid #6366f1',
                    }
                  },
                  '& fieldset': {
                    border: 'none',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#6366f1',
                  }
                }
              }}
              autoFocus
            />
            {state.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {state.error}
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
              Подтверждение данных
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Данные должны совпадать с указанными в учетной записи
            </Alert>
            <TextField
              label="Введите ИИН"
              variant="outlined"
              fullWidth
              value={state.formData.iin}
              onChange={(e) => actions.setIin(e.target.value)}
              disabled={state.loading}
              inputProps={{ maxLength: 12 }}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))',
                  borderRadius: '12px',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 10px rgba(99, 102, 241, 0.15)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.9))',
                  },
                  '&.Mui-focused': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.95))',
                    '& fieldset': {
                      border: '2px solid #6366f1',
                    }
                  },
                  '& fieldset': {
                    border: 'none',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#6366f1',
                  }
                }
              }}
            />
            <TextField
              label="Введите ФИО"
              variant="outlined"
              fullWidth
              value={state.formData.fullName}
              onChange={(e) => actions.setFullName(e.target.value)}
              disabled={state.loading}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))',
                  borderRadius: '12px',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 10px rgba(99, 102, 241, 0.15)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.9))',
                  },
                  '&.Mui-focused': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.95))',
                    '& fieldset': {
                      border: '2px solid #6366f1',
                    }
                  },
                  '& fieldset': {
                    border: 'none',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#6366f1',
                  }
                }
              }}
            />
            {state.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {state.error}
              </Alert>
            )}
          </Box>
        );
      
      case 3:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
              Создайте новый пароль
            </Typography>
            <TextField
              label="Новый пароль"
              type={state.showNewPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              value={state.formData.newPassword}
              onChange={(e) => actions.setNewPassword(e.target.value)}
              disabled={state.loading}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))',
                  borderRadius: '12px',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 10px rgba(99, 102, 241, 0.15)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.9))',
                  },
                  '&.Mui-focused': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.95))',
                    '& fieldset': {
                      border: '2px solid #6366f1',
                    }
                  },
                  '& fieldset': {
                    border: 'none',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#6366f1',
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={actions.toggleShowNewPassword}
                      disabled={state.loading}
                      sx={{
                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        color: '#6366f1',
                        '&:hover': { 
                          transform: 'scale(1.2) rotate(5deg)',
                          color: '#8b5cf6',
                          backgroundColor: 'rgba(99, 102, 241, 0.1)'
                        }
                      }}
                    >
                      {state.showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="Подтвердите пароль"
              type={state.showConfirmPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              value={state.formData.confirmPassword}
              onChange={(e) => actions.setConfirmPassword(e.target.value)}
              disabled={state.loading}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))',
                  borderRadius: '12px',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 10px rgba(99, 102, 241, 0.15)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.9))',
                  },
                  '&.Mui-focused': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)',
                    background: 'linear-gradient(145deg, rgba(255,255,255,1), rgba(255,255,255,0.95))',
                    '& fieldset': {
                      border: '2px solid #6366f1',
                    }
                  },
                  '& fieldset': {
                    border: 'none',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#6366f1',
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={actions.toggleShowConfirmPassword}
                      disabled={state.loading}
                      sx={{
                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        color: '#6366f1',
                        '&:hover': { 
                          transform: 'scale(1.2) rotate(5deg)',
                          color: '#8b5cf6',
                          backgroundColor: 'rgba(99, 102, 241, 0.1)'
                        }
                      }}
                    >
                      {state.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {state.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {state.error}
              </Alert>
            )}
          </Box>
        );

      case 4:
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Zoom in={true} style={{ transitionDelay: '200ms' }}>
              <CheckCircle sx={{ fontSize: 60, color: '#6366f1', mb: 2 }} />
            </Zoom>
            <Typography variant="h5" gutterBottom sx={{ color: '#6366f1', fontWeight: 600 }}>
              Пароль успешно изменен!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Теперь вы можете войти в систему с новым паролем
            </Typography>
            <Button
              variant="contained"
              onClick={handleComplete}
              size="large"
              sx={{
                py: 2,
                px: 4,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                boxShadow: `
                  0 4px 15px rgba(99, 102, 241, 0.3),
                  0 2px 8px rgba(139, 92, 246, 0.2)
                `,
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  transition: 'left 0.6s',
                  zIndex: 1
                },
                '&:hover': {
                  transform: 'translateY(-3px) scale(1.02)',
                  boxShadow: `
                    0 8px 25px rgba(99, 102, 241, 0.4),
                    0 4px 15px rgba(139, 92, 246, 0.3)
                  `,
                  '&::before': {
                    left: '100%'
                  }
                }
              }}
            >
              Продолжить
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Slide}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '20px',
          overflow: 'hidden',
          background: 'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(255,255,255,0.92))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.4)',
          boxShadow: `
            0 8px 32px rgba(0, 0, 0, 0.12),
            0 2px 8px rgba(99, 102, 241, 0.08),
            inset 0 1px 0 rgba(255,255,255,0.6)
          `,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%)',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            zIndex: 1
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        py: 3,
        position: 'relative',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
        zIndex: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          {currentStepData.icon}
          <Typography variant="h6" fontWeight="bold">
            Сброс пароля
          </Typography>
        </Box>
        {state.currentStep > 1 && state.currentStep < 4 && (
          <IconButton
            onClick={actions.prevStep}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.15)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }
            }}
          >
            <ArrowBack />
          </IconButton>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Stepper */}
        <Box sx={{ p: 3, pb: 0 }}>
          <Stepper 
            activeStep={state.currentStep - 1} 
            alternativeLabel
            sx={{
              '& .MuiStepConnector-root': {
                '& .MuiStepConnector-line': {
                  borderTopWidth: '2px',
                  borderColor: '#e5e7eb',
                },
                '&.Mui-active .MuiStepConnector-line': {
                  borderColor: '#6366f1',
                },
                '&.Mui-completed .MuiStepConnector-line': {
                  borderColor: '#6366f1',
                }
              },
              '& .MuiStepIcon-root': {
                color: '#e5e7eb',
                '&.Mui-active': {
                  color: '#6366f1',
                },
                '&.Mui-completed': {
                  color: '#6366f1',
                }
              },
              '& .MuiStepLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500,
                '&.Mui-active': {
                  color: '#6366f1',
                  fontWeight: 600
                },
                '&.Mui-completed': {
                  color: '#6366f1',
                }
              }
            }}
          >
            {PASSWORD_RESET_STEPS.map((step) => (
              <Step key={step.step}>
                <StepLabel>{step.title}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        
        {/* Контент шага */}
        <Fade in={true} timeout={300} key={state.currentStep}>
          <Box>
            {renderStepContent()}
            
          </Box>
        </Fade>
        
        {/* Кнопки управления */}
        {state.currentStep < 4 && (
          <Box sx={{ p: 3, pt: 0, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleClose} 
              disabled={state.loading}
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: '10px',
                color: '#6b7280',
                transition: 'all 0.3s ease',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  color: '#6366f1'
                }
              }}
            >
              Отмена
            </Button>
            <Button
              variant="contained"
              onClick={actions.nextStep}
              disabled={state.loading || !canProceed()}
              sx={{
                minWidth: 120,
                py: 1.5,
                px: 3,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                boxShadow: `
                  0 3px 12px rgba(99, 102, 241, 0.3),
                  0 1px 6px rgba(139, 92, 246, 0.2)
                `,
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `
                    0 6px 20px rgba(99, 102, 241, 0.4),
                    0 3px 10px rgba(139, 92, 246, 0.3)
                  `,
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              {state.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                state.currentStep === 3 ? 'Сохранить' : 'Далее'
              )}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PasswordResetDialog;
